import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from '@/store/index';
import { RootState } from '@/store/index';
import { featureUpdatesActions, Feature } from '@/store/Reducers/featureUpdatesReducer';
import {
  getFeatureUpdates,
  markFeaturesSeen,
  markAllFeaturesSeen,
} from '@/store/asyncActions/featureUpdatesActions';
import { HugeiconsIcon } from '@hugeicons/react';
import { MegaphoneIcon, ArrowRight01Icon, ArrowLeft01Icon, Calendar03Icon } from '@hugeicons/core-free-icons';
import { Badge } from 'antd';
import { Card, CardHeader, CardBody, Modal, ModalContent, ModalBody } from '@heroui/react';
import PopoverBase from 'ui/popover';
import Chip from 'ui/chip';
import FeatureItem from './FeatureItem';
import { useRouter } from 'next/router';

const STALE_TIME = 5 * 60 * 1000;
const MARK_SEEN_DELAY = 2000;

const categoryConfig: Record<string, { label: string; color: 'primary' | 'success' | 'warning' | 'danger' | 'default' }> = {
  ai_tools: { label: 'AI Tools', color: 'primary' },
  dashboard: { label: 'Dashboard', color: 'success' },
  data: { label: 'Data', color: 'warning' },
  fgd_idi: { label: 'FGD / IDI', color: 'danger' },
  question_builder: { label: 'Question Builder', color: 'primary' },
  settings: { label: 'Settings', color: 'default' },
  general: { label: 'General', color: 'default' },
};

const ImageCarousel = ({ images, title, onPreview }: { images: string[]; title: string; onPreview?: (src: string) => void }) => {
  const [current, setCurrent] = useState(0);
  if (!images || images.length === 0) return null;
  return (
    <div className="relative group w-full h-[180px] mt-3">
      <img
        src={images[current]}
        alt={`${title} ${current + 1}`}
        className="h-[180px] w-full object-cover rounded-lg border border-border-color cursor-pointer hover:border-primary/40 transition-all"
        onClick={(e) => { e.stopPropagation(); onPreview?.(images[current]); }}
      />
      {images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent((p) => (p === 0 ? images.length - 1 : p - 1)); }}
            className="absolute left-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/80 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <HugeiconsIcon icon={ArrowLeft01Icon} size={14} color="#4a4a4a" strokeWidth={2} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setCurrent((p) => (p === images.length - 1 ? 0 : p + 1)); }}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/80 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <HugeiconsIcon icon={ArrowRight01Icon} size={14} color="#4a4a4a" strokeWidth={2} />
          </button>
          <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, idx) => (
              <span
                key={idx}
                className={`rounded-full transition-all ${idx === current ? 'w-3 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-white/70'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const FeatureDetail = ({ feature, onBack }: { feature: Feature; onBack: () => void }) => {
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div onClick={(e) => e.stopPropagation()} className="max-h-[440px] overflow-y-auto">
      <button
        onClick={(e) => { e.stopPropagation(); onBack(); }}
        className="flex items-center gap-1 px-5 pt-3 pb-1 text-xs text-default-text-color hover:text-primary-text-color transition-colors sticky top-0 bg-white z-10"
      >
        <HugeiconsIcon icon={ArrowLeft01Icon} size={14} strokeWidth={1.5} />
        Back
      </button>
      <div className="px-5 pb-4">
        <div className="flex items-center gap-2 mt-2">
          <h3 className="text-base font-bold text-primary-text-color">{feature.title}</h3>
          {!feature.seen && (
            <Chip variant="flat" color="primary" size="sm" className="text-[10px] h-4">NEW</Chip>
          )}
        </div>
        <div className="flex items-center gap-3 mt-2">
          {feature.category && (
            <Chip variant="flat" color={categoryConfig[feature.category]?.color || 'default'} size="sm" className="text-[10px] capitalize">
              {categoryConfig[feature.category]?.label || feature.category}
            </Chip>
          )}
          <div className="flex items-center gap-1 text-[11px] text-default-text-color">
            <HugeiconsIcon icon={Calendar03Icon} size={12} strokeWidth={1.5} />
            {formatDate(feature.createdAt)}
          </div>
        </div>
        <p className="text-sm text-primary-text-color leading-relaxed mt-3">
          {feature.description}
        </p>
        <ImageCarousel images={feature.media || []} title={feature.title} onPreview={handleImagePreview} />
      </div>
    </div>
  );
};

const FeatureUpdateWidget = () => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const markSeenTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  // Track feature to restore when modal closes and popover should reopen
  const reopenFeatureRef = useRef<Feature | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const unseenCount = useSelector(
    (state: RootState) => state.featureUpdatesReducer.unseenCount,
  );
  const features = useSelector(
    (state: RootState) => state.featureUpdatesReducer.features,
  );
  const lastFetched = useSelector(
    (state: RootState) => state.featureUpdatesReducer.lastFetched,
  );

  const fetchUpdates = useCallback(async () => {
    const res = await getFeatureUpdates();
    if (res?.data) {
      dispatch(featureUpdatesActions.setFeatures({
        features: res.data.features,
        unseenCount: res.data.unseenCount,
      }));
    }
  }, [dispatch]);

  useEffect(() => { fetchUpdates(); }, []);

  useEffect(() => {
    if (isOpen && lastFetched && Date.now() - lastFetched > STALE_TIME) {
      fetchUpdates();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && unseenCount > 0) {
      markSeenTimerRef.current = setTimeout(async () => {
        const unseenIds = features.filter((f) => !f.seen).map((f) => f.id);
        if (unseenIds.length > 0) {
          await markFeaturesSeen(unseenIds);
          dispatch(featureUpdatesActions.markSeen(unseenIds));
        }
      }, MARK_SEEN_DELAY);
    }
    return () => {
      if (markSeenTimerRef.current) clearTimeout(markSeenTimerRef.current);
    };
  }, [isOpen, unseenCount, features, dispatch]);

  const handleMarkAllSeen = async () => {
    await markAllFeaturesSeen();
    dispatch(featureUpdatesActions.markAllSeen());
  };

  const handleImagePreview = (src: string) => {
    reopenFeatureRef.current = selectedFeature;
    setPreviewImage(src);
  };

  const handleModalClose = () => {
    setPreviewImage(null);
    // Reopen widget where user left off
    const featureToRestore = reopenFeatureRef.current;
    reopenFeatureRef.current = null;
    setTimeout(() => {
      setSelectedFeature(featureToRestore);
      triggerRef.current?.click();
    }, 100);
  };

  return (
  <>
    <PopoverBase
      onClose={() => {
        setIsOpen(false);
        // If closing because of image preview, save the feature to restore later
        if (!previewImage && !reopenFeatureRef.current) {
          setSelectedFeature(null);
        }
      }}
      overrideDefaultStyles={true}
      trigger={
        <div
          ref={triggerRef}
          className="flex items-center justify-center p-1 cursor-pointer text-primary-text-color hover:bg-background-color rounded-md"
          onClick={() => setIsOpen(true)}
        >
          <Badge count={unseenCount} color="#6b83e7" size="small">
            <HugeiconsIcon
              icon={MegaphoneIcon}
              size={20}
              color="currentColor"
              strokeWidth={1.5}
            />
          </Badge>
        </div>
      }
      placement="bottom-end"
    >
      <Card shadow="md" className="w-[400px]" key={selectedFeature ? 'detail' : 'list'}>
        <CardHeader className="flex flex-col px-0 pb-0">
          <div className="flex w-full items-center justify-between px-5 py-2">
            {selectedFeature ? (
              <button
                onClick={(e) => { e.stopPropagation(); setSelectedFeature(null); }}
                className="flex items-center gap-1 text-sm text-default-text-color hover:text-primary-text-color transition-colors"
              >
                <HugeiconsIcon icon={ArrowLeft01Icon} size={16} strokeWidth={1.5} />
                Back
              </button>
            ) : (
              <h4 className="text-large font-medium">Platform Updates</h4>
            )}
            {!selectedFeature && unseenCount > 0 && (
              <button
                onClick={handleMarkAllSeen}
                className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
              >
                Mark all as read
              </button>
            )}
          </div>
        </CardHeader>
        <CardBody className="w-full gap-0 p-0">
          <div className="max-h-[440px] w-full overflow-y-auto scrollbar-thin">
            {selectedFeature ? (
              <div onClick={(e) => e.stopPropagation()} className="px-5 pb-4">
                <div className="flex items-center gap-2 mt-1">
                  <h3 className="text-base font-bold text-primary-text-color">{selectedFeature.title}</h3>
                  {!selectedFeature.seen && (
                    <Chip variant="flat" color="primary" size="sm" className="text-[10px] h-4">NEW</Chip>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {selectedFeature.category && (
                    <Chip variant="flat" color={categoryConfig[selectedFeature.category]?.color || 'default'} size="sm" className="text-[10px] capitalize">
                      {categoryConfig[selectedFeature.category]?.label || selectedFeature.category}
                    </Chip>
                  )}
                  <div className="flex items-center gap-1 text-[11px] text-default-text-color">
                    <HugeiconsIcon icon={Calendar03Icon} size={12} strokeWidth={1.5} />
                    {new Date(selectedFeature.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </div>
                </div>
                <p className="text-sm text-primary-text-color leading-relaxed mt-3">
                  {selectedFeature.description}
                </p>
                <ImageCarousel images={selectedFeature.media || []} title={selectedFeature.title} onPreview={handleImagePreview} />
              </div>
            ) : features.length > 0 ? (
              <div className="p-2 flex flex-col gap-0.5">
                {features.slice(0, 4).map((feature) => (
                  <div key={feature.id} onClick={(e) => { e.stopPropagation(); e.preventDefault(); setSelectedFeature(feature); }} className="cursor-pointer">
                    <FeatureItem feature={feature} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10">
                <HugeiconsIcon icon={MegaphoneIcon} size={32} color="#e0e0e0" strokeWidth={1.5} />
                <p className="text-sm text-default-text-color mt-2">No updates yet</p>
              </div>
            )}
          </div>
          {features.length > 0 && !selectedFeature && (
            <div className="border-t border-border-color px-4 py-2.5">
              <button
                onClick={() => router.push('/whats-new')}
                className="w-full flex items-center justify-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors"
              >
                View All
                <HugeiconsIcon icon={ArrowRight01Icon} size={14} strokeWidth={2} />
              </button>
            </div>
          )}
        </CardBody>
      </Card>
    </PopoverBase>
    <Modal
      classNames={{ closeButton: 'rounded-md top-3 right-3' }}
      isOpen={!!previewImage}
      onClose={handleModalClose}
      size="5xl"
      placement="center"
    >
      <ModalContent className="pt-14 px-6 pb-6 rounded-xl">
        <ModalBody className="p-0 flex items-center justify-center">
          <img
            src={previewImage || ''}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            alt="Preview"
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  </>
  );
};

export default FeatureUpdateWidget;
